import React, { useCallback, useState, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./components/assets/css/LiveChat.css";
import "./custom.css";
import { sanitizeHTML } from './utils/sanitize';

// ─── Lazy-loaded route components for code splitting ──────────────────
const Home = lazy(() => import('./components/pages/mobile/Home'));
const Login = lazy(() => import('./components/pages/mobile/Login'));
const SignUp = lazy(() => import('./components/pages/mobile/SignUp'));
const Registion = lazy(() => import('./components/pages/desktop/Registion'));
const MAccount = lazy(() => import('./components/pages/mobile/MAccount'));
const Profile = lazy(() => import('./components/pages/mobile/Profile'));
const MDeposit = lazy(() => import('./components/pages/mobile/MDeposit'));
const MWithdrawal = lazy(() => import('./components/pages/mobile/MWithdrawal'));
const BettingRecord = lazy(() => import('./components/pages/mobile/BettingRecord'));
const TransactionRecord = lazy(() => import('./components/pages/mobile/TransactionRecord'));
const TurnoverRecord = lazy(() => import('./components/pages/mobile/TurnoverRecord'));
const RefrrelProgram = lazy(() => import('./components/pages/mobile/RefrrelProgram'));
const ResetPassword = lazy(() => import('./components/pages/mobile/ResetPassword'));
const MobileInbox = lazy(() => import('./components/pages/mobile/MobileInbox'));
const VIPConvert = lazy(() => import('./components/pages/mobile/VIPConvert'));
const Games = lazy(() => import('./components/pages/mobile/Games'));
const GameRun = lazy(() => import('./components/pages/mobile/GameRun'));
const MobilePromotion = lazy(() => import('./components/pages/mobile/MobilePromotion'));
const ChatHome = lazy(() => import('./components/Chat/ChatHome'));
const ChatHistory = lazy(() => import('./components/Chat/ChatHistory'));
const ChatBox = lazy(() => import('./components/Chat/ChatBox'));
const EXHome = lazy(() => import('./components/exchanges/EXHome'));
const EXHomeVelki = lazy(() => import('./components/exchanges/EXHomeVelki'));
const MatchOdds = lazy(() => import('./components/exchanges/MatchOdds'));
const ExAccount = lazy(() => import('./components/exchanges/ExAccount'));
const InPlay = lazy(() => import('./components/exchanges/InPlay'));
const PaymentTimer = lazy(() => import('./components/pages/mobile/common/PaymentTimer'));
const ForgotPassword = lazy(() => import('./components/pages/mobile/ForgotPassword'));
const StoreCode = lazy(() => import('./StoreCodeComponents'));
const MakePayment = lazy(() => import('./components/pages/mobile/common/MakePayment'));
const Maintenance = lazy(() => import('./components/pages/mobile/common/Maintenance'));
const FreeSpinPage = lazy(() => import('./free-spin/FreeSpinPage'));
const NineWiket = lazy(() => import('./components/pages/mobile/NineWiket'));

// ─── Eagerly loaded (needed immediately) ──────────────────────────────
import Splash from './splash/Splash';
import APIS from './components/pages/mobile/common/apis';
import GoToTop from './components/common/GoTop';
import { Modal } from "react-bootstrap";

// ─── Loading fallback ──────────────────────────────────────────────────
const PageLoader = () => (
  <div className="loading-overlay">
    <div className="loading-wrap" style={{ display: 'flex' }}>
      <div className="loading"><div /><div /></div>
      <p>Loading...</p>
    </div>
  </div>
);

// ─── PrivateRoute – DEFINED OUTSIDE App to prevent remounting ──────────
// Uses localStorage directly to avoid stale closure issues
function PrivateRoute({ element }) {
  const isLoggedIn = !!localStorage.getItem('token');
  return isLoggedIn ? element : <Navigate to="/login" replace />;
}

// ─── Balance display component – React state instead of DOM manipulation
function BalanceDisplay({ balance }) {
  if (balance == null) return null;
  return <>{balance}</>;
}

// ─── App Component ─────────────────────────────────────────────────────
function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
  const [showContent, setShowContent] = useState(false);
  const [pwaIconUrl, setPwaIconUrl] = useState(null);
  const [pwaSiteName, setPwaSiteName] = useState(null);
  const location = useLocation();
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  // ── /#/9wkt exit handler ────────────────────────────────────────────
  useEffect(() => {
    if (window.location.href.includes('/#/9wkt')) {
      const token = localStorage.getItem('token');
      const ctrl = new AbortController();
      fetch(`${process.env.REACT_APP_API_URL}/api/9wkt/exit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        signal: ctrl.signal,
      }).finally(() => {
        const cleanUrl = window.location.href.split('/#/9wkt')[0];
        window.location.replace(cleanUrl);
      });
      return () => ctrl.abort();
    }
  }, []);

  const [globalData, setGlobalData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('error');
  const [balance, setBalance] = useState(() => parseFloat(localStorage.getItem('balance')) || 0);
  const [refreshPvtData, setRefreshPvtData] = useState(false);
  const balanceRef = useRef(balance);
  useEffect(() => { balanceRef.current = balance; }, [balance]);

  // ─── showAlert ──────────────────────────────────────────────────────
  const showAlert = useCallback((message = false, type = 'error') => {
    if (!message) {
      setShowModal(false);
      setModalMessage('');
      return;
    }
    setShowModal(true);
    setModalType(type || 'error');
    setModalMessage(message);
  }, []);

  // ─── setFavicon ─────────────────────────────────────────────────────
  const setFavicon = useCallback((url) => {
    if (!url) return;
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = url;
  }, []);

  const [cssLoading, setCssLoading] = useState(false);

  // ─── Fetch favicon + logo ───────────────────────────────────────────
  // Uses AbortController for cleanup. Logo data is also fetched by APIS,
  // so this only sets favicon/PWA icon.
  useEffect(() => {
    const ctrl = new AbortController();
    const origin = window.location.hostname.replace(/^www\./, '');
    const fetchFavicon = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/logo?origin=${encodeURIComponent(origin)}`,
          { signal: ctrl.signal }
        );
        if (!response.ok) throw new Error("Failed to fetch logo data");
        const data = await response.json();
        const item = Array.isArray(data) ? data[0] : data;
        if (item?.favicon) {
          const faviconUrl = item.favicon.startsWith('http')
            ? item.favicon
            : `${process.env.REACT_APP_API_URL}/storage/${item.favicon}`;
          setFavicon(faviconUrl);
          const pwaIcon = item?.logo
            ? (item.logo.startsWith('http') ? item.logo : `${process.env.REACT_APP_API_URL}/storage/${item.logo}`)
            : faviconUrl;
          setPwaIconUrl(pwaIcon);
        }
      } catch (error) {
        if (error.name !== 'AbortError') console.error("Error fetching favicon:", error);
      }
    };
    fetchFavicon();
    return () => ctrl.abort();
  }, [setFavicon]);

  // ─── Fetch general site data (from APIS - kept for PWA site name) ───
  // Note: /api/general is also fetched by APIS component. This duplicates it but
  // is needed for PWA manifest before APIS completes. Uses AbortController.
  useEffect(() => {
    const ctrl = new AbortController();
    const fetchGeneral = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/general`, {
          headers,
          signal: ctrl.signal,
        });
        if (!response.ok) throw new Error("Failed to fetch General");
        const data = await response.json();
        if (data && data[0]) {
          document.title = data[0].site_title;
          setPwaSiteName(data[0].site_title);
          localStorage.setItem('whatsapp', data[0].whatsapp);
          localStorage.setItem('liveChat', data[0].live_chat);
          localStorage.setItem('intercom', data[0].intercom);
          localStorage.setItem('tawkId', data[0]?.tawk_id);
        }
      } catch (error) {
        if (error.name !== 'AbortError') console.error("Error fetching General:", error);
      }
    };
    fetchGeneral();
    return () => ctrl.abort();
  }, []);

  // ─── PWA manifest (memoized to reduce Blob creation) ────────────────
  useEffect(() => {
    if (!pwaSiteName && !pwaIconUrl) return;
    const manifestData = {
      short_name: pwaSiteName || document.title,
      name: pwaSiteName || document.title,
      start_url: '/',
      display: 'standalone',
      theme_color: '#000000',
      background_color: '#ffffff',
      icons: pwaIconUrl
        ? [
            { src: pwaIconUrl, sizes: '192x192', type: 'image/png' },
            { src: pwaIconUrl, sizes: '512x512', type: 'image/png' },
          ]
        : [],
    };
    const blob = new Blob([JSON.stringify(manifestData)], { type: 'application/json' });
    const manifestUrl = URL.createObjectURL(blob);
    let manifestLink = document.querySelector("link[rel='manifest']");
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      document.head.appendChild(manifestLink);
    }
    manifestLink.href = manifestUrl;
    return () => URL.revokeObjectURL(manifestUrl);
  }, [pwaSiteName, pwaIconUrl]);

  // ─── fetchBalance with React state instead of DOM manipulation ──────
  const fetchBalance = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/balance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        const bal = parseFloat(data.balance) || 0;
        setBalance(bal);
        localStorage.setItem('balance', String(bal));
      } else if (response.status === 401) {
        handleLogout();
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    setIsLoggedIn(false);
    window.location.href = '/';
  }, []);

  // ─── Balance polling (15s instead of 10s to reduce server load) ─────
  useEffect(() => {
    if (!isLoggedIn) return;
    fetchBalance();
    const interval = setInterval(fetchBalance, 15000);
    return () => clearInterval(interval);
  }, [isLoggedIn, fetchBalance]);

  // ─── CSS loading ────────────────────────────────────────────────────
  useEffect(() => {
    let extraCssLink;
    let isMounted = true;

    const loadExtraCss = () => {
      return new Promise((resolve, reject) => {
        if (location.pathname === "/make-payment") {
          extraCssLink = document.createElement("link");
          extraCssLink.rel = "stylesheet";
          extraCssLink.href = "./extra.css";
          extraCssLink.id = "extra-css";
          extraCssLink.onload = resolve;
          extraCssLink.onerror = reject;
          document.head.appendChild(extraCssLink);
        } else {
          resolve();
        }
      });
    };

    const loadOtherCss = async () => {
      await import("./components/assets/css/flipclock.css");
      await import("./components/assets/css/Mobile.css");
    };

    const loadAllCss = async () => {
      setCssLoading(true);
      try {
        await Promise.all([loadExtraCss(), loadOtherCss()]);
      } catch (err) {
        console.error("CSS load error:", err);
      } finally {
        if (isMounted) setCssLoading(false);
      }
    };

    loadAllCss();

    return () => {
      isMounted = false;
      const existingLink = document.getElementById("extra-css");
      if (existingLink) document.head.removeChild(existingLink);
    };
  }, [location.pathname]);

  // ─── GTM (Google Tag Manager) ───────────────────────────────────────
  const [gtmId, setGtmId] = useState(null);

  useEffect(() => {
    const id = globalData?.general?.google_tag;
    if (!id) return;
    setGtmId(id);

    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
    }

    const oldScript = document.getElementById("gtm-script");
    if (oldScript) oldScript.remove();

    const script = document.createElement("script");
    script.id = "gtm-script";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
    document.head.appendChild(script);

    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });

    return () => {
      const s = document.getElementById("gtm-script");
      if (s) s.remove();
    };
  }, [globalData?.general?.google_tag]); // FIXED: Specific dependency instead of entire globalData object

  // ─── Memoized splash callback ───────────────────────────────────────
  const handleSplashComplete = useCallback(() => {
    setShowContent(true);
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }, []);

  // ─── Memoized route props to prevent unnecessary re-renders ─────────
  const homeElement = useMemo(() => {
    return globalData?.general?.site_status == 0 ? <Maintenance /> : <Home globalData={globalData} />;
  }, [globalData?.general?.site_status, globalData]);

  const signUpSliders = useMemo(() => globalData?.sliders?.main || [], [globalData?.sliders?.main]);

  const accountProps = useMemo(() => ({
    fetchBalance,
    userData: globalData?.userData,
    general: globalData?.general || {},
    sociallink: globalData?.socialLinks || {},
  }), [fetchBalance, globalData?.userData, globalData?.general, globalData?.socialLinks]);

  const depositProps = useMemo(() => ({
    usdtRate: globalData?.general?.usdt_rate,
    hasEWallet: globalData?.hasEWallet,
    userData: globalData?.userData || [],
    transactionData: globalData?.transactions || [],
    items: globalData?.paymentMethods || [],
    promotions: globalData?.promotions?.activePromotion || [],
  }), [globalData?.general?.usdt_rate, globalData?.hasEWallet, globalData?.userData, globalData?.transactions, globalData?.paymentMethods, globalData?.promotions?.activePromotion]);

  const withdrawalProps = useMemo(() => ({
    general: globalData?.general,
    hasEWallet: globalData?.hasEWallet,
    userData: globalData?.userData || [],
    transactionData: globalData?.transactions || [],
    items: globalData?.paymentMethods || [],
  }), [globalData?.general, globalData?.hasEWallet, globalData?.userData, globalData?.transactions, globalData?.paymentMethods]);

  const transactionRecordProps = useMemo(() => ({
    setRefreshPvtData,
    transactionData: globalData?.transactions || [],
  }), [globalData?.transactions, setRefreshPvtData]);

  const turnoverRecordProps = useMemo(() => ({
    turnovers: globalData?.turnovers || [],
  }), [globalData?.turnovers]);

  const refrrelProgramProps = useMemo(() => ({
    showAlert,
    userData: globalData?.userData,
  }), [showAlert, globalData?.userData]);

  const promotionProps = useMemo(() => ({
    promotions: globalData?.promotions?.allPromotions || [],
  }), [globalData?.promotions?.allPromotions]);

  const makePaymentProps = useMemo(() => ({
    hasEWallet: globalData?.hasEWallet,
    userData: globalData?.userData || [],
  }), [globalData?.hasEWallet, globalData?.userData]);

  // ─── Cleanup on unmount ─────────────────────────────────────────────
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // ─── Render ─────────────────────────────────────────────────────────
  return (
    <div className="App">
      {gtmId && (
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
            title="gtm"
          ></iframe>
        </noscript>
      )}
      <GoToTop />
      <APIS setGlobalData={setGlobalData} refreshPvtData={refreshPvtData} />

      <Splash onSplashComplete={handleSplashComplete} />

      {cssLoading && <Splash />}

      {showContent && (
        <Suspense fallback={<PageLoader />}>
        <>
          <Routes>
            <Route path="/" element={homeElement} />
            <Route path="/signup" element={<SignUp slidersMain={signUpSliders} />} />
            <Route path="/login" element={<Login />} />
            <Route path="/games" element={<Games />} />
            <Route path="/forgot-password" element={<ForgotPassword showAlert={showAlert} />} />
            <Route path="/account" element={<PrivateRoute element={<MAccount {...accountProps} />} />} />
            <Route path="/9wiket" element={<NineWiket />} />
            <Route path="/profile" element={<PrivateRoute element={<Profile />} />} />
            <Route path="/deposit" element={<PrivateRoute element={<MDeposit {...depositProps} />} />} />
            <Route path="/withdrawal" element={<PrivateRoute element={<MWithdrawal {...withdrawalProps} />} />} />
            <Route path="/betting-record" element={<PrivateRoute element={<BettingRecord />} />} />
            <Route path="/transaction-record" element={<PrivateRoute element={<TransactionRecord {...transactionRecordProps} />} />} />
            <Route path="/turnover-record" element={<PrivateRoute element={<TurnoverRecord {...turnoverRecordProps} />} />} />
            <Route path="/refrrel-program" element={<PrivateRoute element={<RefrrelProgram {...refrrelProgramProps} />} />} />
            <Route path="/reset-password" element={<PrivateRoute element={<ResetPassword showAlert={showAlert} />} />} />
            <Route path="/inbox" element={<PrivateRoute element={<MobileInbox />} />} />
            <Route path="/vip" element={<PrivateRoute element={<VIPConvert />} />} />
            <Route path="/gamerun" element={<PrivateRoute element={<GameRun />} />} />
            <Route path="/promotions" element={<PrivateRoute element={<MobilePromotion {...promotionProps} />} />} />
            <Route path="/chat" element={<PrivateRoute element={<ChatHome />} />} />
            <Route path="/chat-list" element={<PrivateRoute element={<ChatHistory />} />} />
            <Route path="/chat-box/:chatId" element={<PrivateRoute element={<ChatBox />} />} />
            <Route path="/odds-view" element={<PrivateRoute element={<MatchOdds />} />} />
            <Route path="/exchange" element={<PrivateRoute element={<EXHome />} />} />
            <Route path="/velki" element={<PrivateRoute element={<EXHomeVelki />} />} />
            <Route path="/exchange-account" element={<PrivateRoute element={<ExAccount />} />} />
            <Route path="/exchange-inplay" element={<PrivateRoute element={<InPlay />} />} />
            <Route path="/payment-request" element={<PrivateRoute element={<PaymentTimer />} />} />
            <Route path="/referral_code" element={<StoreCode codeType="referral_code" />} />
            <Route path="/affiliate_code" element={<StoreCode codeType="affiliate_code" />} />
            <Route path="/agent_code" element={<StoreCode codeType="agent_code" />} />
            <Route path="/make-payment" element={<MakePayment {...makePaymentProps} />} />
            <Route path="/free-spin" element={<FreeSpinPage />} />
            <Route path="/register" element={<Registion />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          <div>
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
              <Modal.Header closeButton>
                <Modal.Title>
                  {modalType === 'success' ? 'Success' : modalType === 'info' ? 'Notice' : 'Error'}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(modalMessage) }} />
              </Modal.Body>
            </Modal>
          </div>
        </>
        </Suspense>
      )}
    </div>
  );
}

export default App;
